export const JOBS_SAVED_SEARCHES_STORAGE_KEY = 'jobsSavedSearches';
export const JOBS_SAVED_SEARCHES_VERSION = 1;

export const JOBS_VIEW_KEYS = ['priority', 'active', 'jobDone', 'archived', 'all'];

export const EMPTY_FILTER_SNAPSHOT = {
  searchQuery: '',
  selectedStatuses: [],
  selectedPriorities: [],
  selectedStates: [],
  selectedYear: 'all',
  selectedTrades: [],
  selectedTags: [],
  selectedClients: [],
  selectedDateStart: null,
  selectedDateEnd: null,
  unlockedOnly: false,
};

export const createEmptyViewBucket = () => ({
  defaultId: null,
  searches: [],
});

const createEmptyStorage = () => ({
  version: JOBS_SAVED_SEARCHES_VERSION,
  views: JOBS_VIEW_KEYS.reduce((acc, viewKey) => {
    acc[viewKey] = createEmptyViewBucket();
    return acc;
  }, {}),
});

const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;

export const getJobsSavedSearchesStorageKey = (companyId) => {
  if (companyId == null || companyId === '') return null;
  return `${JOBS_SAVED_SEARCHES_STORAGE_KEY}:${companyId}`;
};

/** One-time: move legacy unscoped key into the first company bucket that loads it. */
const migrateLegacySavedSearchesIfNeeded = (companyId) => {
  const key = getJobsSavedSearchesStorageKey(companyId);
  if (!key || typeof window === 'undefined') return;
  try {
    if (window.localStorage.getItem(key)) return;
    const legacy = window.localStorage.getItem(JOBS_SAVED_SEARCHES_STORAGE_KEY);
    if (!legacy) return;
    window.localStorage.setItem(key, legacy);
    window.localStorage.removeItem(JOBS_SAVED_SEARCHES_STORAGE_KEY);
  } catch {
    // ignore quota / private mode
  }
};

const normalizeStringArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => item != null && String(item).length > 0)
    .map((item) => String(item));
};

const normalizeTagEntries = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => item && item.id != null)
    .map((item) => ({
      id: item.id,
      name: typeof item.name === 'string' ? item.name : String(item.id),
    }));
};

const normalizeClientEntries = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => item && item.id != null)
    .map((item) => ({
      id: item.id,
      client_name: typeof item.client_name === 'string' ? item.client_name : 'Unknown',
    }));
};

export const captureFilterSnapshot = (filterState) => ({
  searchQuery: typeof filterState.searchQuery === 'string' ? filterState.searchQuery : '',
  selectedStatuses: normalizeStringArray(filterState.selectedStatuses),
  selectedPriorities: normalizeStringArray(filterState.selectedPriorities),
  selectedStates: normalizeStringArray(filterState.selectedStates),
  selectedYear: typeof filterState.selectedYear === 'string' ? filterState.selectedYear : 'all',
  selectedTrades: normalizeStringArray(filterState.selectedTrades),
  selectedTags: normalizeTagEntries(filterState.selectedTags),
  selectedClients: normalizeClientEntries(filterState.selectedClients),
  selectedDateStart: filterState.selectedDateStart instanceof Date
    ? filterState.selectedDateStart.toISOString()
    : null,
  selectedDateEnd: filterState.selectedDateEnd instanceof Date
    ? filterState.selectedDateEnd.toISOString()
    : null,
  unlockedOnly: filterState.unlockedOnly === true,
});

export const parseDateField = (iso) => {
  if (!iso || typeof iso !== 'string') return null;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const serializeDateField = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }
  if (typeof value === 'string' && value.length > 0) {
    const parsed = parseDateField(value);
    return parsed ? parsed.toISOString() : null;
  }
  return null;
};

export const normalizeFilterSnapshot = (snapshot) => {
  const source = snapshot && typeof snapshot === 'object' ? snapshot : {};
  const dateStart = serializeDateField(source.selectedDateStart);
  const dateEnd = serializeDateField(source.selectedDateEnd);
  const selectedYear = typeof source.selectedYear === 'string' ? source.selectedYear : 'all';

  return {
    searchQuery: typeof source.searchQuery === 'string' ? source.searchQuery : '',
    selectedStatuses: normalizeStringArray(source.selectedStatuses),
    selectedPriorities: normalizeStringArray(source.selectedPriorities),
    selectedStates: normalizeStringArray(source.selectedStates),
    selectedYear: (dateStart || dateEnd) ? 'all' : selectedYear,
    selectedTrades: normalizeStringArray(source.selectedTrades),
    selectedTags: normalizeTagEntries(source.selectedTags),
    selectedClients: normalizeClientEntries(source.selectedClients),
    selectedDateStart: dateStart,
    selectedDateEnd: dateEnd,
    unlockedOnly: source.unlockedOnly === true,
  };
};

export const sanitizeFilterSnapshot = (snapshot, { jobsViewStatusIds, tagOptions = [], clientOptions } = {}) => {
  const normalized = normalizeFilterSnapshot(snapshot);

  if (Array.isArray(jobsViewStatusIds) && jobsViewStatusIds.length > 0) {
    const allowedStatuses = new Set(jobsViewStatusIds.map((statusId) => String(statusId)));
    normalized.selectedStatuses = normalized.selectedStatuses.filter((status) => allowedStatuses.has(status));
  }

  const tagOptionMap = new Map((tagOptions || []).map((tag) => [String(tag.id), tag]));
  normalized.selectedTags = normalized.selectedTags
    .map((tag) => {
      const resolved = tagOptionMap.get(String(tag.id));
      if (resolved) {
        return { id: resolved.id, name: resolved.name };
      }
      if (tag.id != null && isNonEmptyString(tag.name)) {
        return { id: tag.id, name: tag.name };
      }
      return null;
    })
    .filter(Boolean);

  // Strict: keep only clients in clientOptions. Omit clientOptions to skip (list not ready).
  if (Array.isArray(clientOptions)) {
    const clientOptionMap = new Map(clientOptions.map((client) => [String(client.id), client]));
    normalized.selectedClients = normalized.selectedClients
      .map((client) => {
        const resolved = clientOptionMap.get(String(client.id));
        if (!resolved) return null;
        return {
          id: resolved.id,
          client_name: resolved.client_name || client.client_name || 'Unknown',
        };
      })
      .filter(Boolean);
  }

  return normalized;
};

export const arraysEqualById = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const aIds = a.map((item) => String(item.id)).sort();
  const bIds = b.map((item) => String(item.id)).sort();
  return aIds.every((id, index) => id === bIds[index]);
};

const arraysEqual = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
};

export const snapshotsMatch = (left, right) => {
  const a = normalizeFilterSnapshot(left);
  const b = normalizeFilterSnapshot(right);

  return (
    a.searchQuery === b.searchQuery
    && arraysEqual(a.selectedStatuses, b.selectedStatuses)
    && arraysEqual(a.selectedPriorities, b.selectedPriorities)
    && arraysEqual(a.selectedStates, b.selectedStates)
    && a.selectedYear === b.selectedYear
    && arraysEqual(a.selectedTrades, b.selectedTrades)
    && arraysEqualById(a.selectedTags, b.selectedTags)
    && arraysEqualById(a.selectedClients, b.selectedClients)
    && a.selectedDateStart === b.selectedDateStart
    && a.selectedDateEnd === b.selectedDateEnd
    && a.unlockedOnly === b.unlockedOnly
  );
};

const normalizeSearchEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return null;
  if (!isNonEmptyString(entry.id) || !isNonEmptyString(entry.name)) return null;

  return {
    id: entry.id,
    name: entry.name.trim(),
    createdAt: typeof entry.createdAt === 'string' ? entry.createdAt : new Date().toISOString(),
    updatedAt: typeof entry.updatedAt === 'string' ? entry.updatedAt : new Date().toISOString(),
    filters: normalizeFilterSnapshot(entry.filters),
  };
};

const normalizeViewBucket = (bucket) => {
  const source = bucket && typeof bucket === 'object' ? bucket : {};
  const searches = Array.isArray(source.searches)
    ? source.searches.map(normalizeSearchEntry).filter(Boolean)
    : [];

  const defaultId = typeof source.defaultId === 'string' ? source.defaultId : null;
  const hasDefault = defaultId && searches.some((search) => search.id === defaultId);

  return {
    defaultId: hasDefault ? defaultId : null,
    searches,
  };
};

export const normalizeSavedSearchesStorage = (raw) => {
  const empty = createEmptyStorage();
  if (!raw || typeof raw !== 'object') return empty;

  const views = { ...empty.views };
  JOBS_VIEW_KEYS.forEach((viewKey) => {
    views[viewKey] = normalizeViewBucket(raw.views?.[viewKey]);
  });

  return {
    version: JOBS_SAVED_SEARCHES_VERSION,
    views,
  };
};

export const loadJobsSavedSearches = (companyId) => {
  try {
    if (!companyId) return createEmptyStorage();
    migrateLegacySavedSearchesIfNeeded(companyId);
    const key = getJobsSavedSearchesStorageKey(companyId);
    const stored = window.localStorage.getItem(key);
    if (!stored) return createEmptyStorage();
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object') return createEmptyStorage();
    if (parsed.version !== JOBS_SAVED_SEARCHES_VERSION) return createEmptyStorage();
    return normalizeSavedSearchesStorage(parsed);
  } catch {
    return createEmptyStorage();
  }
};

export const persistJobsSavedSearches = (companyId, data) => {
  const normalized = normalizeSavedSearchesStorage(data);
  const key = getJobsSavedSearchesStorageKey(companyId);
  if (!key) return normalized;
  try {
    window.localStorage.setItem(key, JSON.stringify(normalized));
  } catch {
    // ignore quota / private mode
  }
  return normalized;
};

/**
 * Drop inaccessible clients from every saved search for this company and persist when changed.
 * Call after the live client list has loaded (primary-role admin only at the call site).
 */
export const pruneSavedSearchesAgainstClients = (companyId, storage, clientOptions = []) => {
  const normalized = normalizeSavedSearchesStorage(storage);
  if (!companyId) {
    return { storage: normalized, changed: false };
  }

  let changed = false;
  const views = { ...normalized.views };

  JOBS_VIEW_KEYS.forEach((viewKey) => {
    const bucket = views[viewKey];
    const searches = bucket.searches.map((search) => {
      const sanitizedFilters = sanitizeFilterSnapshot(search.filters, { clientOptions });
      if (arraysEqualById(search.filters.selectedClients, sanitizedFilters.selectedClients)) {
        const namesChanged = search.filters.selectedClients.some((client, index) => (
          String(client.client_name || '')
          !== String(sanitizedFilters.selectedClients[index]?.client_name || '')
        ));
        if (!namesChanged) return search;
        changed = true;
        return {
          ...search,
          filters: sanitizedFilters,
          updatedAt: new Date().toISOString(),
        };
      }
      changed = true;
      return {
        ...search,
        filters: sanitizedFilters,
        updatedAt: new Date().toISOString(),
      };
    });
    views[viewKey] = { ...bucket, searches };
  });

  if (!changed) {
    return { storage: normalized, changed: false };
  }

  const nextStorage = persistJobsSavedSearches(companyId, {
    ...normalized,
    views,
  });
  return { storage: nextStorage, changed: true };
};

export const getViewBucket = (storage, viewKey) => {
  const normalized = normalizeSavedSearchesStorage(storage);
  if (!JOBS_VIEW_KEYS.includes(viewKey)) return createEmptyViewBucket();
  return normalized.views[viewKey];
};

const updateViewBucket = (companyId, storage, viewKey, updater) => {
  const normalized = normalizeSavedSearchesStorage(storage);
  if (!JOBS_VIEW_KEYS.includes(viewKey)) return normalized;

  const nextBucket = updater(normalized.views[viewKey]);
  const nextStorage = {
    ...normalized,
    views: {
      ...normalized.views,
      [viewKey]: normalizeViewBucket(nextBucket),
    },
  };

  return persistJobsSavedSearches(companyId, nextStorage);
};

export const createSearchId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `search-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const upsertSearch = (companyId, storage, viewKey, { id, name, filters, setAsDefault = false }) => {
  const trimmedName = typeof name === 'string' ? name.trim() : '';
  if (!trimmedName) {
    throw new Error('Saved search name is required.');
  }

  const normalizedFilters = normalizeFilterSnapshot(filters);
  const now = new Date().toISOString();
  let savedSearchId = id || null;

  const nextStorage = updateViewBucket(companyId, storage, viewKey, (bucket) => {
    const existingIndex = id ? bucket.searches.findIndex((search) => search.id === id) : -1;
    const nextId = existingIndex >= 0 ? bucket.searches[existingIndex].id : createSearchId();
    savedSearchId = nextId;

    const nextSearch = {
      id: nextId,
      name: trimmedName,
      createdAt: existingIndex >= 0 ? bucket.searches[existingIndex].createdAt : now,
      updatedAt: now,
      filters: normalizedFilters,
    };

    const searches = existingIndex >= 0
      ? bucket.searches.map((search, index) => (index === existingIndex ? nextSearch : search))
      : [...bucket.searches, nextSearch];

    return {
      defaultId: setAsDefault ? nextId : bucket.defaultId,
      searches,
    };
  });

  return { storage: nextStorage, searchId: savedSearchId };
};

export const deleteSearch = (companyId, storage, viewKey, searchId) => updateViewBucket(companyId, storage, viewKey, (bucket) => ({
  defaultId: bucket.defaultId === searchId ? null : bucket.defaultId,
  searches: bucket.searches.filter((search) => search.id !== searchId),
}));

export const setDefaultSearch = (companyId, storage, viewKey, searchId) => updateViewBucket(companyId, storage, viewKey, (bucket) => {
  const exists = bucket.searches.some((search) => search.id === searchId);
  return {
    ...bucket,
    defaultId: exists ? searchId : bucket.defaultId,
  };
});

export const clearDefaultSearch = (companyId, storage, viewKey) => updateViewBucket(companyId, storage, viewKey, (bucket) => ({
  ...bucket,
  defaultId: null,
}));

export const renameSearch = (companyId, storage, viewKey, searchId, name) => {
  const trimmedName = typeof name === 'string' ? name.trim() : '';
  if (!trimmedName) {
    throw new Error('Saved search name is required.');
  }

  return updateViewBucket(companyId, storage, viewKey, (bucket) => ({
    ...bucket,
    searches: bucket.searches.map((search) => (
      search.id === searchId
        ? { ...search, name: trimmedName, updatedAt: new Date().toISOString() }
        : search
    )),
  }));
};

export const findSearchById = (storage, viewKey, searchId) => {
  const bucket = getViewBucket(storage, viewKey);
  return bucket.searches.find((search) => search.id === searchId) || null;
};

export const getDefaultSearchForView = (storage, viewKey) => {
  const bucket = getViewBucket(storage, viewKey);
  if (!bucket.defaultId) return null;
  return bucket.searches.find((search) => search.id === bucket.defaultId) || null;
};

export const getJobsViewLabel = (viewKey) => {
  switch (viewKey) {
    case 'priority':
      return 'Priority';
    case 'active':
      return 'Active';
    case 'jobDone':
      return 'Job Done';
    case 'archived':
      return 'Archived';
    case 'all':
      return 'All';
    default:
      return 'Jobs';
  }
};
